import React from 'react';
import { useTranslation } from '../../i18n';
import { CategoryResults } from '../shared';
import { GameConfig } from '../../config/types';
import { TeamsState, TeamId } from '../../types/teams';
import GameCarousel from './GameCarousel';
import { LobbyQrPanel, PremiumButtonAnim } from './LobbyQrPanel';
import { LobbyPlayersColumn } from './LobbyPlayersColumn';

interface LobbyPlayer {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  teamId?: 'A' | 'B';
  isBot?: boolean;
}

interface HostLobbyProps {
  roomName: string;
  games: GameConfig[];
  currentGameType?: string;
  selectedGameType?: string;
  votes: Record<string, number>;
  categoryVotes: Record<string, number>;
  players: LobbyPlayer[];
  teams?: TeamsState;
  showTeamAssign: boolean;
  myPlayerId?: string;
  isPremium: boolean;
  hostIsPlaying: boolean;
  premiumButtonAnim: PremiumButtonAnim;
  brandingDisplayName?: string;
  brandingLogoUrl?: string;
  onSelectGame: (gameType: string) => void;
  onPremiumClick: () => void;
  onZoomQr: () => void;
  onJoinRoom?: () => void;
  onOpenSettings?: () => void;
  onToggleHostPlaying: () => void;
  onSetTeam: (playerId: string, teamId: TeamId | null) => void;
  onAddAiPlayer?: () => void;
  onRemoveAiPlayer?: (playerId: string) => void;
  addAiDisabled?: boolean;
  pendingAiCount?: number;
}

export const HostLobby: React.FC<HostLobbyProps> = ({
  roomName,
  games,
  currentGameType,
  selectedGameType,
  votes,
  categoryVotes,
  players,
  teams,
  showTeamAssign,
  myPlayerId,
  isPremium,
  hostIsPlaying,
  premiumButtonAnim,
  brandingDisplayName,
  brandingLogoUrl,
  onSelectGame,
  onPremiumClick,
  onZoomQr,
  onJoinRoom,
  onOpenSettings,
  onToggleHostPlaying,
  onSetTeam,
  onAddAiPlayer,
  onRemoveAiPlayer,
  addAiDisabled,
  pendingAiCount = 0,
}) => {
  const { t } = useTranslation();

  return (
    <div className="lobby-shell lobby-shell--host">
      <div className="choose-game-section">
        <div className="choose-game-header">
          <div className="choose-game-header-line" />
          <h2 className="choose-game-header-text">{t('lobby.gameSelection.chooseGame')}</h2>
          <div className="choose-game-header-line" />
        </div>

        <div className="choose-game-carousel-wrap">
          <GameCarousel
            games={games}
            currentGameType={selectedGameType || currentGameType}
            votes={votes}
            onSelectGame={onSelectGame}
            onPremiumClick={onPremiumClick}
            isPremiumRoom={isPremium}
          />
        </div>
      </div>

      <div className="host-main-layout lobby-top-row">
        <LobbyQrPanel
          roomName={roomName}
          isPremium={isPremium}
          hostIsPlaying={hostIsPlaying}
          premiumButtonAnim={premiumButtonAnim}
          brandingDisplayName={brandingDisplayName}
          brandingLogoUrl={brandingLogoUrl}
          onZoomQr={onZoomQr}
          onOpenPremium={onPremiumClick}
          onOpenSettings={onOpenSettings}
          onJoinRoom={onJoinRoom}
          onToggleHostPlaying={onToggleHostPlaying}
        />

        <LobbyPlayersColumn
          players={players}
          teams={teams}
          showTeamAssign={showTeamAssign}
          canAssignAny
          myPlayerId={myPlayerId}
          onSetTeam={onSetTeam}
          onAddAiPlayer={onAddAiPlayer}
          onRemoveAiPlayer={onRemoveAiPlayer}
          addAiDisabled={addAiDisabled}
          pendingAiCount={pendingAiCount}
        />

        <div className="host-right-column host-categories-column">
          <CategoryResults
            gameType={currentGameType || ''}
            categoryVotes={categoryVotes}
            collapsible
            maxVisible={5}
          />
        </div>
      </div>
    </div>
  );
};
